import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { useFormContext, Controller } from 'react-hook-form'
import { Input, message } from 'antd'
import TextArea from 'antd/es/input/TextArea'

import backgroundExampleImg from '../images/background-example.jpg'

import { VendorProfileFormType } from '../type'

import { IMG_STORAGE_URL } from '@src/utils/constants'
import { copyTxt } from '@src/utils/utils'
import { resizeImage, compressImage } from '@src/utils/image'

import SectionWrapper from './SectionWrapper'
import SVGLoader from '@src/components/svg/SVGLoader'
import InputField from '@src/pages/brand/BrandProfilePage/components/InputField'
import ExampleLabel from './ExampleLabel'
import ErrorMessage from './ErrorMessage'

type BrabdInfoSectionPropsType = {
	username: string
	profileImgState: Blob | null | undefined
	setProfileImg: Dispatch<SetStateAction<Blob | null | undefined>>
	backgroundImgState: Blob | null | undefined
	setBackgroundImg: Dispatch<SetStateAction<Blob | null | undefined>>
}

const BrandInfoSection = ({
	username,
	profileImgState,
	setProfileImg,
	backgroundImgState,
	setBackgroundImg,
}: BrabdInfoSectionPropsType) => {
	const {
		register,
		getValues,
		setValue,
		control,
		watch,
		formState: { errors },
	} = useFormContext<VendorProfileFormType>()

	const profileImg = getValues('profileImg')
	const watchBackgroundImg = watch('backgroundImg')

	const profileImgSource = useMemo(() => {
		if (profileImgState) {
			return URL.createObjectURL(profileImgState)
		}
		if (profileImg) {
			return `${IMG_STORAGE_URL}/commerce/vendor/profile_img/${profileImg}`
		}
		return null
	}, [profileImg, profileImgState])

	const backgroundImgSource = useMemo(() => {
		if (backgroundImgState) {
			return URL.createObjectURL(backgroundImgState)
		}
		if (watchBackgroundImg) {
			return `${IMG_STORAGE_URL}/commerce/vendor/background_img/${watchBackgroundImg}`
		}
		return null
	}, [backgroundImgState, watchBackgroundImg])

	const handleClickCopyURL = useCallback(() => {
		if (username) {
			copyTxt(`https://preview.motiners.com?path=${username}`)
				.then(() => message.info('복사되었습니다.', 2))
				.catch(() => message.error('잠시 후 다시 시도해주세요.', 2))
		}
	}, [username])

	const handleChangeProfileImg = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target

		if (files) {
			const resizedImg = await resizeImage(files[0], 560, Infinity)
			const imgFile =
				resizedImg.size > 3000000
					? await compressImage(resizedImg, {
							maxSizeMB: 3,
							maxWidthOrHeight: Infinity,
							alwaysKeepResolution: true,
					  })
					: resizedImg

			setProfileImg(imgFile)
		}
	}

	const handleChangeBackgroundImg = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target

		if (files) {
			const resizedImg = await resizeImage(files[0], 750, Infinity)
			const imgFile =
				resizedImg.size > 3000000
					? await compressImage(resizedImg, {
							maxSizeMB: 3,
							maxWidthOrHeight: Infinity,
							alwaysKeepResolution: true,
					  })
					: resizedImg

			setBackgroundImg(imgFile)
		}
	}

	const handleClickRemoveBackgroundImg = () => {
		setBackgroundImg(null)
		setValue('backgroundImg', null, { shouldDirty: true })
	}

	return (
		<SectionWrapper title="브랜드 정보">
			<Section>
				<BrandProfileCard>
					<ProfileImgBlock>
						{profileImgSource ? (
							<ProfileImg src={profileImgSource} />
						) : (
							<BlankProfileImg>
								<ProfileIcon name="profile" />
							</BlankProfileImg>
						)}
						<HiddenInput
							type="file"
							id="profile_img"
							{...register('profileImg', { onChange: handleChangeProfileImg })}
						/>
						<EditProfileBtn htmlFor="profile_img">
							<SVGLoader name="pencel" />
						</EditProfileBtn>
					</ProfileImgBlock>
					<CardRight>
						<UserName>{username}</UserName>
						<VendorStoreLink>
							<VendorStoreLinkText>{`https://preview.motiners.com?path=${username}`}</VendorStoreLinkText>
							<CopyText onClick={handleClickCopyURL}>복사</CopyText>
						</VendorStoreLink>
					</CardRight>
				</BrandProfileCard>
				<InputField
					label={
						<BackgroundImgLabelBlock>
							<BackgroundImgLabelText>브랜드 페이지 배경 이미지</BackgroundImgLabelText>
							<ExampleLabel exampleImg={backgroundExampleImg} />
						</BackgroundImgLabelBlock>
					}
				>
					<BackgroundImgBlock>
						{backgroundImgSource ? (
							<>
								<BackgroundImg src={backgroundImgSource} />
								<RemoveBackgroundImgBtn type="button" onClick={handleClickRemoveBackgroundImg}>
									<SVGLoader name="closeIcon" />
								</RemoveBackgroundImgBtn>
							</>
						) : (
							<BlankBackgroundImgLabel htmlFor="background_img">
								<PlusIcon>
									<SVGLoader name="plus" />
								</PlusIcon>
								<SizeGuied>750*400px</SizeGuied>
								<HiddenInput
									type="file"
									id="background_img"
									{...register('backgroundImg', { onChange: handleChangeBackgroundImg })}
								/>
							</BlankBackgroundImgLabel>
						)}
					</BackgroundImgBlock>
				</InputField>
				<InputField label="브랜드 명">
					<Controller
						control={control}
						name="name"
						render={({ field }) => {
							return (
								<Input
									{...field}
									status={errors.name && 'error'}
									placeholder="브랜드 명을 입력해주세요."
								/>
							)
						}}
					/>
					<ErrorMessage errorMessage={errors.name?.message} />
				</InputField>
				<InputField label="사이트 URL">
					<Controller
						control={control}
						name="vendorStoreLink"
						render={({ field }) => {
							return (
								<Input
									{...field}
									status={errors.vendorStoreLink && 'error'}
									placeholder="사이트 URL을 입력해주세요."
								/>
							)
						}}
					/>
					<ErrorMessage errorMessage={errors.vendorStoreLink?.message} />
				</InputField>
				<InputField label="브랜드 한줄 소개">
					<Controller
						control={control}
						name="companyDescription"
						render={({ field }) => {
							return (
								<TextArea
									{...field}
									cols={2}
									maxLength={64}
									status={errors.companyDescription && 'error'}
									placeholder="기업 소개글을 입력해주세요.(글자수 65자 제한)"
									style={{ resize: 'none' }}
								/>
							)
						}}
					/>
				</InputField>
				<ErrorMessage errorMessage={errors.companyDescription?.message} />
			</Section>
		</SectionWrapper>
	)
}

const Section = styled.div`
	display: flex;
	flex-direction: column;
`

const BrandProfileCard = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 3.6rem;
`

const ProfileImgBlock = styled.div`
	position: relative;
	margin-right: 2.3rem;
`

const profileImgStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 6.4rem;
	height: 6.4rem;
	border-radius: 50%;
`

const BlankProfileImg = styled.div`
	${profileImgStyle}
	background-color: #EFEFEF;
	border: 1px solid #c4c4c4;
`

const EditProfileBtn = styled.label`
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	bottom: 0;
	right: 0;
	border: none;
	background-color: rgba(51, 55, 57, 0.8);
	width: 2.3rem;
	height: 2.3rem;
	border-radius: 50%;
	overflow: hidden;
	cursor: pointer;

	& > svg {
		color: #fff;
	}
`

const ProfileImg = styled.img`
	${profileImgStyle}
`

const ProfileIcon = styled(SVGLoader)`
	color: #c5c5c5;
	width: 4rem;
	height: 4rem;
`

const HiddenInput = styled.input`
	display: none;
`

const CardRight = styled.div`
	display: flex;
	flex-direction: column;
`

const UserName = styled.span`
	color: #000;
	font-family: Pretendard;
	font-size: 1.6rem;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	letter-spacing: 0.2px;
	margin-bottom: 0.9rem;
`
const VendorStoreLink = styled.div`
	display: flex;
	align-items: center;
`

const VendorStoreLinkText = styled.span`
	color: #4e4e4e;
	font-family: 'Pretendard';
	font-size: 1.3rem;
	font-style: normal;
	font-weight: 500;
	line-height: normal;
	letter-spacing: 0.183px;
	text-decoration-line: underline;
	margin-right: 0.9rem;
`

const CopyText = styled.span`
	color: #2f80ed;
	font-family: 'Pretendard';
	font-size: 1.3rem;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
	letter-spacing: 0.2px;
	cursor: pointer;
`

const BackgroundImgLabelBlock = styled.div`
	display: flex;
	align-items: center;
`
const BackgroundImgLabelText = styled.span`
	margin-right: 1.1rem;
`

const BackgroundImgBlock = styled.div`
	position: relative;
	width: 24rem;
	height: 12.7rem;
	border-radius: 1rem;
	overflow: hidden;
`

const BackgroundImg = styled.img`
	width: 24rem;
	height: 12.7rem;
	object-fit: cover;
`

const RemoveBackgroundImgBtn = styled.button`
	position: absolute;
	top: 0.5rem;
	right: 0.58rem;
	border: none;
	border-radius: 50%;
	width: 1.8rem;
	height: 1.8rem;
	background-color: #4e4e4e;

	& > svg {
		color: #fff;
	}
`

const BlankBackgroundImgLabel = styled.label`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background-color: #e2e5ee;
	width: 100%;
	height: 100%;
	cursor: pointer;
`

const PlusIcon = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: #fff;
	border-radius: 50%;
	width: 3.2rem;
	height: 3.2rem;

	& > svg {
		color: #8b98be;
	}
`

const SizeGuied = styled.span`
	margin-top: 0.6rem;
	color: #4e4e4e;
	font-family: Pretendard;
	font-size: 1.2rem;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
	letter-spacing: 0.174px;
`

export default BrandInfoSection
